import { LOADING_TEXT, translate as t } from '@thoughtspot/i18n';
import cx from 'classnames';
import _ from 'lodash';
import React from 'react';
import { getUniqueKey } from '../_utils';
import { Button } from '../button';
import { IconID } from '../icon';
import { Input } from '../input';
import { Menu } from '../menu';
import { Popover } from '../popover';
import { SafeHTML } from '../safe-html';
import { Colors, Typography } from '../typography';
import styles from './select.module.scss';
export var SELECT_DATA_TEST_ID;
(function (SELECT_DATA_TEST_ID) {
    SELECT_DATA_TEST_ID["DROPDOWN_SEARCH"] = "dropdown-search";
    SELECT_DATA_TEST_ID["EMPTY_OPTION"] = "empty-option";
    SELECT_DATA_TEST_ID["DROPDOWN_FOOTER"] = "dropdown-footer";
})(SELECT_DATA_TEST_ID || (SELECT_DATA_TEST_ID = {}));
export const defaultSelectedValue = {
    id: '',
    label: '',
    imagePath: '',
    subText: '',
};
export const resetSelectedOptionId = 'reset-selected-option';
const selectContainerClass = cx(styles.select, styles.selectOpen);
const OptionHighlighted = { id: '', label: '' };
/**
 * factory method to create select component
 * @param selectprop : input props to create a jsx for type of select
 * @param setSearchTerm : setter function to search search term if isSearchEnabled
 * @param dropDownHeader : render header if the type === 'dropdown'
 * @param onSelectCallback : select callback from type === 'dropdown'
 * @param optionHighlighted : hightlight option state for accessibility
 * @param selectMenuRef : setRef for select menu to handle document Event hander)
 * @returns JSX factory based on the type of select props
 */
export const createBaseSelect = ({ optionsGroups, id = '', classList = {
    selectHeadClass: '',
    selectCaret: '',
    selectValue: '',
    selectBodyClass: '',
}, isSearchEnabled = false, isMenuVisible = true, type = 'dropdown', selectedOptions = [], multiSelect = false, emptyText = 'No records found', footer, showFooterIfNoData = false, loading, dropdownWidth, bottomOffset = 0, showLabelTooltip, labelTruncationLimit, canSearchSubText = false, PreviousSelectedObjects = {}, onKeyPressCB = _.noop, shouldAutoFocusSearch = false, inputRef, enableKeyboardNavigation = false, footerRef, searchPlaceholder = 'Search...', stickyFooter, variant = 'default', ...restProps }, { searchTerm = '', setSearchTerm = _.noop, onSelectCallback = _.noop, dropDownHeader = null, optionHighlighted = OptionHighlighted, selectMenuRef = { current: null }, dropDownHeaderRef = { current: null }, customDropdownContent, enableScroll = true, stickyFooterClickCallback = _.noop, }) => {
    const enableMultiSelect = (optionId) => {
        if (multiSelect) {
            return {
                iconName: selectedOptions.indexOf(optionId) >= 0
                    ? IconID.CHECKMARK
                    : IconID.EMPTY,
            };
        }
        return {};
    };
    const enableRichSubtext = variant === 'richSubtext';
    const renderOptions = (optionGroup, showSeperator = true) => (React.createElement(Menu.Group, { showSeparator: showSeperator, key: getUniqueKey(optionGroup) },
        optionGroup.title && !optionGroup.titleButton && (React.createElement(Typography, { variant: "p-bold", className: styles.groupTitle }, optionGroup.title)),
        optionGroup.title && optionGroup.titleButton && (React.createElement("div", { className: styles.groupTitleWithButton },
            React.createElement(Typography, { variant: "p-bold", noMargin: true }, optionGroup.title),
            React.createElement(Button, { icon: optionGroup.titleButton.icon, onClick: optionGroup.titleButton.onClick, size: "xs", type: "secondary", "data-testid": optionGroup.titleButton.icon, className: styles.groupButton }))),
        optionGroup.emptyState && optionGroup.options.length === 0 && (React.createElement("div", { className: styles.noRecords, "data-testid": SELECT_DATA_TEST_ID.EMPTY_OPTION },
            React.createElement("div", { className: styles.emptyState }, optionGroup.emptyState))),
        optionGroup.options.map((option) => {
            const { id: OptionId, label, children, data, disabled: isDisabled, onSelect = _.noop, tooltip, subText, rightText, iconName, imagePath, active, } = option;
            // Handle loading text automatically
            const isLoading = label === LOADING_TEXT;
            const menuItemChildren = isLoading ? (React.createElement("div", { className: styles.loadingTextContainer },
                React.createElement(SafeHTML, { html: LOADING_TEXT }))) : (children);
            return (React.createElement(Menu.Item, { key: getUniqueKey(`${label}-${OptionId}`), id: OptionId, label: label, data: data, imagePath: imagePath, active: (optionHighlighted &&
                    optionHighlighted.id === OptionId) ||
                    active, disabled: isDisabled, onSelect: (menuData) => {
                    var _a;
                    onSelect(menuData);
                    onSelectCallback(option);
                    // to focus the dropdown header after selecting menu item using enter key
                    (_a = dropDownHeaderRef === null || dropDownHeaderRef === void 0 ? void 0 : dropDownHeaderRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, ...enableMultiSelect(OptionId), "data-tooltip-content": tooltip, "data-tooltip-position": "center", "data-tooltip-align": "top", "data-testid": "select-option", subText: subText, rightText: rightText, iconName: iconName, showLabelTooltip: showLabelTooltip, labelTruncationLimit: labelTruncationLimit, enableRichSubtext: enableRichSubtext }, menuItemChildren));
        })));
    const renderNoRecords = () => {
        const footerClass = cx(styles.footer, {
            [styles.withMultiselect]: multiSelect,
        });
        return (React.createElement("div", { className: styles.noRecords },
            React.createElement("img", { src: "/node_modules/@thoughtspot/radiant-styles/public/img/illustrations/no-result-found.svg", className: styles.noResultFoundIcon, alt: "No Result Found" }),
            React.createElement(Typography, { variant: "p-bold", color: Colors.gray, className: styles.noSuggestionText }, emptyText),
            showFooterIfNoData && !!footer && (React.createElement("div", { className: footerClass }, footer))));
    };
    const renderOptionGroup = (optionGroups) => {
        if (!optionGroups.length) {
            return renderNoRecords();
        }
        const footerClass = cx(styles.footer, {
            [styles.withMultiselect]: multiSelect,
            [styles.stickyFooterContainer]: stickyFooter,
        });
        return (
        // createBaseSelect is only called from select-menu.tsx and select-dropdown.tsx,
        // both of which implement their own keyboard navigation, so we can disable it in Menu
        React.createElement(Menu, { id: id, show: isMenuVisible, disableKeyboardNavigation: !enableKeyboardNavigation, onMenuItemKeyPressCB: onKeyPressCB },
            optionGroups.map((option, index) => renderOptions(option, !(optionGroups.length - 1 === index))),
            !!footer && (React.createElement("div", { role: "button", className: footerClass, ref: footerRef, tabIndex: 0, onKeyDown: onKeyPressCB, onClick: () => {
                    if (stickyFooter) {
                        stickyFooterClickCallback();
                    }
                }, "data-testid": SELECT_DATA_TEST_ID.DROPDOWN_FOOTER }, footer))));
    };
    const updateSearchTerm = (searchTermString) => setSearchTerm(searchTermString);
    const handleInputChange = (event) => {
        updateSearchTerm(event.target.value);
    };
    /** TODO this will be replace by React Input Component */
    const renderSearchInput = () => isMenuVisible &&
        isSearchEnabled && (React.createElement(Input.Text, { inputRef: inputRef, value: searchTerm, prefixIcon: IconID.MAGNIFYING_GLASS, placeholder: searchPlaceholder, ariaLabel: t('dropdown_search_placeholder'), onChange: handleInputChange, className: styles.searchInput, "data-testid": SELECT_DATA_TEST_ID.DROPDOWN_SEARCH, autoFocus: shouldAutoFocusSearch, onTab: onKeyPressCB, onEscape: onKeyPressCB }));
    const renderLoader = () => {
        return (React.createElement("div", { className: styles.loading },
            React.createElement(Typography, { variant: "h5", color: Colors.gray },
                "Loading",
                React.createElement("span", { className: styles.dots }, "."))));
    };
    const getContent = () => {
        return (customDropdownContent !== null && customDropdownContent !== void 0 ? customDropdownContent : (React.createElement("div", { className: styles.popoverWrapper },
            renderSearchInput(),
            loading
                ? renderLoader()
                : renderOptionGroup(optionsGroups))));
    };
    const getContentWidth = () => {
        let contentWidth = dropdownWidth;
        if (dropdownWidth !== 'defaultWidth') {
            return contentWidth;
        }
        if (multiSelect) {
            if (isSearchEnabled) {
                contentWidth = 'multiSelectWithSearchBarWidth';
            }
            else {
                contentWidth = 'multiSelectWidth';
            }
        }
        else if (isSearchEnabled) {
            contentWidth = 'singleSelectWithSearchBarWidth';
        }
        else {
            contentWidth = 'singleSelectWidth';
        }
        return contentWidth;
    };
    const popoverWrapper = () => (React.createElement(Popover, { popperRef: selectMenuRef, targetRef: dropDownHeaderRef, content: getContent(), isVisible: isMenuVisible, align: "bottom-start", contentWidth: getContentWidth(), appendToBody: true, enableScroll: enableScroll, className: classList === null || classList === void 0 ? void 0 : classList.selectBodyClass, bottomOffset: bottomOffset }));
    /** Use case to remove padding and margin from body class
     * if the select type is menu
     */
    const selectBodyClass = cx(styles.selectBody, {
        [styles.selectMenuBody]: type === 'menu',
        [classList.selectBodyClass]: type === 'menu' && (classList === null || classList === void 0 ? void 0 : classList.selectBodyClass),
    });
    return (
    // Do not add data-testid here so that the caller can
    //  pass their own data-testid
    React.createElement("div", { className: selectContainerClass, ...restProps },
        dropDownHeader,
        React.createElement("div", { className: selectBodyClass, role: "button", "aria-label": "Expand dropdown options" }, type === 'dropdown' ? (popoverWrapper()) : (React.createElement(React.Fragment, null,
            renderSearchInput(),
            renderOptionGroup(optionsGroups))))));
};
//# sourceMappingURL=base-select.js.map